import React, { useContext, useState } from "react";
import { Button, IconButton, Stack, TextField } from "@mui/material";
import { mapStyles } from "../libs/styles";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { AppContext } from "../contexts/AppContext";
import CloseIcon from "@mui/icons-material/Close";
import useBreakpoints from "../hooks/useBreakpoints";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { errorUtil } from "../libs/formikError";
import LoadingButton from "@mui/lab/LoadingButton";
import { SignUpFormModel, signUpRequest } from "../api/authApi";
import { getSnackOptions } from "../libs/snackbar";
import { useSnackbar } from "notistack";
import { ApiResponseError } from "../typings/interfaces";
import { AxiosError } from "axios";

const requiredMessage = "Acest câmp este obligatoriu";

const validationSchema = Yup.object({
  username: Yup.string().required(requiredMessage),
  email: Yup.string().email().required(requiredMessage),
  password: Yup.string().required(requiredMessage),
  rePassword: Yup.string()
    .required(requiredMessage)
    .oneOf([Yup.ref("password")], "Parola nu este corecta"),
  phoneNumber: Yup.string().required(requiredMessage),
});

const FORM_DEFAULT_VALUES: SignUpFormModel = {
  username: "",
  email: "",
  password: "",
  rePassword: "",
  phoneNumber: "",
};

interface SignUpDialogProps {
  open?: boolean;
  onClose?: (openSignIn: boolean) => void;
  sx?: Style;
}

const SignUpDialog: React.FC<SignUpDialogProps> = (props) => {
  const { open, onClose = () => {}, sx } = props;
  const [ctx, updateCtx] = useContext(AppContext);
  const [loading, setLoading] = useState<boolean>(false);
  const { isMobile } = useBreakpoints();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = async (data: SignUpFormModel) => {
    try {
      const res = await signUpRequest(data);
      // console.log(res);
      onClose(true);
      enqueueSnackbar(
        t("components.SignUpDialog.signUpSuccessHelper"),
        getSnackOptions("success", {
          heading: t("components.SignUpDialog.signUpSuccess") ?? "Success",
        })
      );
      navigate("/");
    } catch (e: any | AxiosError) {
      enqueueSnackbar(
        e.response.data.error.message,
        getSnackOptions("error", {
          heading: t("components.SignUpDialog.signUpError") ?? "Error",
        })
      );
    }
  };

  const {
    values,
    handleSubmit,
    errors,
    touched,
    isValid,
    setValues,
    setFieldValue,
    setFieldTouched,
    validateForm,
  } = useFormik({
    initialValues: FORM_DEFAULT_VALUES,
    validationSchema,
    validateOnMount: true,
    onSubmit: onSubmit,
  });

  const errorHandling = errorUtil(touched, errors);

  const handleChange = async (name: string, value: any, touch?: boolean) => {
    await setFieldValue(name, value);
    if (touch) {
      await setFieldTouched(name, true);
    }
  };

  const handleBlur = async (name: string, touch?: boolean) => {
    await setFieldTouched(name, touch);
  };

  if (!open) return null;

  return (
    <>
      <Dialog
        open={open}
        onClose={() => onClose(false)}
        maxWidth="md"
        fullScreen={isMobile}
        sx={styles.dialog}
      >
        <DialogTitle sx={styles.dialogTitle}>
          {t("components.SignUpDialog.title")}

          <IconButton
            aria-label="close"
            onClick={() => onClose(false)}
            sx={styles.close}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={styles.dialogContent}>
          <Stack sx={styles.form} component="form" onSubmit={handleSubmit}>
            <TextField
              label={t("components.SignUpDialog.username")}
              value={values.username}
              onChange={(ev) => handleChange("username", ev.target.value)}
              onBlur={() => handleBlur("username", true)}
              error={errorHandling.isInvalid("username")}
              required
              sx={styles.input}
            />
            <TextField
              label={t("components.SignUpDialog.email")}
              value={values.email}
              onChange={(ev) => handleChange("email", ev.target.value)}
              onBlur={() => handleBlur("email", true)}
              error={errorHandling.isInvalid("email")}
              required
              sx={styles.input}
            />
            <TextField
              label={t("components.SignUpDialog.password")}
              value={values.password}
              onChange={(ev) => handleChange("password", ev.target.value)}
              onBlur={() => handleBlur("password", true)}
              error={errorHandling.isInvalid("password")}
              required
              sx={styles.input}
              type="password"
            />
            <TextField
              label={t("components.SignUpDialog.rePassword")}
              value={values.rePassword}
              onChange={(ev) => handleChange("rePassword", ev.target.value)}
              onBlur={() => handleBlur("rePassword", true)}
              error={errorHandling.isInvalid("rePassword")}
              required
              sx={styles.input}
              type="password"
            />
            <TextField
              label={t("components.SignUpDialog.phoneNumber")}
              value={values.phoneNumber}
              onChange={(ev) => handleChange("phoneNumber", ev.target.value)}
              onBlur={() => handleBlur("phoneNumber", true)}
              error={errorHandling.isInvalid("phoneNumber")}
              required
              sx={styles.input}
            />
            <TextField type="submit" sx={{ display: "none" }} />
          </Stack>
        </DialogContent>
        <DialogActions sx={styles.dialogFooter}>
          <Button
            variant="text"
            onClick={() => {
              onClose(true);
            }}
          >
            {t("components.SignUpDialog.signInButton")}
          </Button>
          <LoadingButton
            onClick={() => onSubmit(values)}
            type="button"
            variant="contained"
            disabled={!isValid}
            loading={loading}
            sx={styles.submit}
          >
            {t("components.SignUpDialog.signUpButton")}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SignUpDialog;

const styles = mapStyles({
  root: {},
  dialog: {},
  dialogTitle: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    px: "12px",
    py: 1,
  },
  dialogContent: {
    display: "flex",
    paddingBottom: 1,
    px: "12px",
  },
  dialogFooter: {
    px: "12px",
    paddingBottom: "12px",
    justifyContent: "space-between",
  },
  close: {
    marginRight: -1,
  },
  form: {
    width: "400px",
    gap: 1,
    mt: 1,
  },
  input: {
    width: "100%",
  },
  submit: {
    minWidth: "min(25%, 100px)",
  },
});
